import type { SelfServeOnboarding } from "@redotech/admin-sdk/rpc/schema/onboardings/get-self-serve-onboardings";

import {
  STEP_CONFIG_WITH_SUBSTEPS,
  type StepConfigWithSubsteps,
} from "./constants";
import type { CustomRange } from "./types";

const dealSizeFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const priceFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const lastActivityFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const DEFAULT_CUSTOM_RANGE_DAYS = 30;

export const createDefaultCustomRange = (): CustomRange => {
  const now = new Date();
  const start = new Date(now.getTime() - DEFAULT_CUSTOM_RANGE_DAYS * DAY_IN_MS);

  return { start: start.toISOString(), end: now.toISOString() };
};

export const sumStepDurations = (onboarding: SelfServeOnboarding) =>
  Object.values(onboarding.onboardingSteps).reduce((acc, step) => {
    return acc + (step.durationSeconds ?? 0);
  }, 0);

export const formatDealSize = (value: number | null): string => {
  if (value == null) {
    return "—";
  }
  return dealSizeFormatter.format(value);
};

export const formatPrice = (value: number | null): string => {
  if (value == null) {
    return "—";
  }
  return priceFormatter.format(value);
};

export const formatDuration = (durationSeconds: number): string => {
  if (!durationSeconds || durationSeconds === 0) {
    return "—";
  }

  if (durationSeconds < 60) {
    // Less than 60 seconds - show as seconds
    return `${durationSeconds}s`;
  }

  const totalMinutes = Math.floor(durationSeconds / 60);
  const seconds = durationSeconds % 60;

  if (totalMinutes < 60) {
    // Less than 60 minutes - show as minutes (and seconds if > 0)
    if (seconds > 0) {
      return `${totalMinutes}m ${seconds}s`;
    }
    return `${totalMinutes}m`;
  }

  // 60+ minutes - show as hours and minutes
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (minutes > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${hours}h`;
};

export const formatLastActivity = (date: Date): string =>
  lastActivityFormatter.format(date);

export const toTitleCase = (value: string) =>
  value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const formatSubstepName = (value: string) =>
  value.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

export const formatProcessingDelay = (delayDays: number) => {
  if (!delayDays || delayDays === 0) {
    return "0 days";
  }

  return `${delayDays} day${delayDays === 1 ? "" : "s"}`;
};

// Types for stepsConfig from configuration
interface StepsConfiguration {
  steps: Record<string, string[]>;
  skipBilling?: boolean | null;
  skipPricing?: boolean | null;
  customCoveragePrice?: number | null;
}

/**
 * Filter STEP_CONFIG_WITH_SUBSTEPS based on custom configuration.
 * Returns only the steps/substeps that are in the configuration.
 * Falls back to full config if no custom config provided.
 *
 * Handles:
 * - Custom step/substep selection via stepsConfig.steps
 * - skipBilling flag removes "billing" substep from review step
 * - skipPricing flag removes pricing substeps (industry-selection, coverage-price, confirmation)
 * - customCoveragePrice removes "industry-selection" substep from returns-onboarding step
 */
export function getVisibleStepConfig(
  stepsConfig: StepsConfiguration | null | undefined,
): StepConfigWithSubsteps[] {
  const hasCustomSteps =
    stepsConfig?.steps && Object.keys(stepsConfig.steps).length > 0;
  const skipBilling = stepsConfig?.skipBilling ?? false;
  const skipPricing = stepsConfig?.skipPricing ?? false;
  const hasCustomCoveragePrice = stepsConfig?.customCoveragePrice != null;

  // No custom config and no special flags - return default (all steps)
  if (
    !hasCustomSteps &&
    !skipBilling &&
    !skipPricing &&
    !hasCustomCoveragePrice
  ) {
    return STEP_CONFIG_WITH_SUBSTEPS;
  }

  const configStepIds = hasCustomSteps
    ? new Set(Object.keys(stepsConfig.steps))
    : null;

  return (
    STEP_CONFIG_WITH_SUBSTEPS.filter((step) => {
      // Always include "submit" (Go Live) for completion tracking
      if (step.dataKey === "submit") {
        return true;
      }
      // If no custom steps config, include all steps
      if (!configStepIds) {
        return true;
      }
      // Check if step is in custom config
      return configStepIds.has(step.dataKey);
    })
      .map((step, index) => {
        let filteredSubsteps = step.substeps;

        // Handle customCoveragePrice - remove "industry-selection" substep from returns-onboarding
        // When custom coverage price is set, industry selection is not shown
        if (hasCustomCoveragePrice && step.dataKey === "returns-onboarding") {
          filteredSubsteps = filteredSubsteps.filter(
            (substep) => substep.key !== "industry-selection",
          );
        }

        // Handle skipPricing - remove pricing substeps
        // Pricing substeps: industry-selection, coverage-price, confirmation
        if (skipPricing) {
          if (step.dataKey === "returns-onboarding") {
            filteredSubsteps = filteredSubsteps.filter(
              (substep) => substep.key !== "industry-selection",
            );
          }
          if (step.dataKey === "coverage") {
            filteredSubsteps = filteredSubsteps.filter(
              (substep) =>
                substep.key !== "coverage-price" &&
                substep.key !== "confirmation",
            );
          }
        }

        // Handle skipBilling - remove "billing" substep from review step
        if (skipBilling && step.dataKey === "review") {
          filteredSubsteps = filteredSubsteps.filter(
            (substep) => substep.key !== "billing",
          );
        }

        // If custom substeps are specified for this step, further filter to only those
        const configSubsteps = stepsConfig?.steps?.[step.dataKey];
        if (configSubsteps && configSubsteps.length > 0) {
          filteredSubsteps = filteredSubsteps.filter((substep) =>
            configSubsteps.includes(substep.key),
          );
        }

        return { ...step, stepNumber: index + 1, substeps: filteredSubsteps };
      })
      // Exclude steps with 0 substeps (except "submit" which inherently has none)
      // This handles edge cases where all substeps in a step are filtered out
      .filter((step) => step.dataKey === "submit" || step.substeps.length > 0)
      // Re-number steps after filtering
      .map((step, index) => ({ ...step, stepNumber: index + 1 }))
  );
}

/**
 * Calculate total expected substeps from visible step config
 */
export function calculateExpectedSubstepsFromConfig(
  visibleSteps: StepConfigWithSubsteps[],
): number {
  return visibleSteps.reduce((sum, step) => {
    // For "submit" step (inherently has no substeps), count as 1
    // This step tracks completion via step-level visited property
    if (step.dataKey === "submit") {
      return sum + 1;
    }
    // For steps that had all substeps removed (e.g., by skipBilling), count as 0
    // Don't count steps with no substeps as progress
    return sum + step.substeps.length;
  }, 0);
}
